// src/App.jsx
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import ScrollToTop from './components/common/ScrollToTop';

// Only import critical components directly
import Home from './pages/Home'; // Keep home page for LCP

// Lazy load all other components
const MainLayout = lazy(() => import('./components/common/MainLayout'));

// Public pages - lazy loaded
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Contact = lazy(() => import('./pages/Contact'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Blog = lazy(() => import('./pages/Blog'));

// Blog articles - lazy loaded
const BlogTaxonomyGuide = lazy(() => import('./pages/blog/TaxonomyGuide'));
const AIVsManualProductCategorization = lazy(() => import('./pages/blog/AIVsManualProductCategorization'));
const GoogleShoppingTaxonomy2025Guide = lazy(() => import('./pages/blog/GoogleShoppingTaxonomy2025Guide'));
const HiddenCostOfBadProductCategorization = lazy(() => import('./pages/blog/HiddenCostOfBadProductCategorization'));

// Protected pages - lazy loaded
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Mapping = lazy(() => import('./pages/Mapping'));
const MappingReview = lazy(() => import('./pages/MappingReview'));
const Taxonomies = lazy(() => import('./pages/Taxonomies'));
const TaxonomyDetail = lazy(() => import('./pages/TaxonomyDetail'));
const TaxonomyCreate = lazy(() => import('./pages/TaxonomyCreate'));
const TaxonomyEdit = lazy(() => import('./pages/TaxonomyEdit'));
const Settings = lazy(() => import('./pages/Settings'));
const SessionsList = lazy(() => import('./pages/SessionsList'));
const Usage = lazy(() => import('./pages/Usage'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Lazy wrapper component
const LazyWrapper = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

const App = () => {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Home page - NOT lazy loaded for best LCP */}
        <Route path="/" element={<Home />} />
        
        {/* Public routes - lazy loaded */}
        <Route path="/login" element={
          <LazyWrapper>
            <Login />
          </LazyWrapper>
        } />
        <Route path="/register" element={
          <LazyWrapper>
            <Register />
          </LazyWrapper>
        } />
        <Route path="/pricing" element={
          <LazyWrapper>
            <Pricing />
          </LazyWrapper>
        } />
        <Route path="/contact" element={
          <LazyWrapper>
            <Contact />
          </LazyWrapper>
        } />
        <Route path="/verify-email" element={
          <LazyWrapper>
            <VerifyEmail />
          </LazyWrapper>
        } />
        <Route path="/forgot-password" element={
          <LazyWrapper>
            <ForgotPassword />
          </LazyWrapper>
        } />
        <Route path="/reset-password" element={
          <LazyWrapper>
            <ResetPassword />
          </LazyWrapper>
        } />
        <Route path="/faq" element={
          <LazyWrapper>
            <FAQ />
          </LazyWrapper>
        } />
        <Route path="/privacy" element={
          <LazyWrapper>
            <Privacy />
          </LazyWrapper>
        } />
        <Route path="/terms" element={
          <LazyWrapper>
            <Terms />
          </LazyWrapper>
        } />
        <Route path="/blog" element={
          <LazyWrapper>
            <Blog />
          </LazyWrapper>
        } />
        
        {/* Blog article routes - lazy loaded */}
        <Route path="/blog/taxonomy-guide" element={
          <LazyWrapper>
            <BlogTaxonomyGuide />
          </LazyWrapper>
        } />
        <Route path="/blog/ai-vs-manual-product-categorization" element={
          <LazyWrapper>
            <AIVsManualProductCategorization />
          </LazyWrapper>
        } />
        <Route path="/blog/google-shopping-taxonomy-2025-guide" element={
          <LazyWrapper>
            <GoogleShoppingTaxonomy2025Guide />
          </LazyWrapper>
        } />
        <Route path="/blog/hidden-cost-of-bad-product-categorization" element={
          <LazyWrapper>
            <HiddenCostOfBadProductCategorization />
          </LazyWrapper>
        } />
        
        {/* Redirect old taxonomy-guide URL to new blog location */}
        <Route path="/taxonomy-guide" element={<Navigate to="/blog/taxonomy-guide" replace />} />
        
        {/* Protected routes - lazy loaded */}
        <Route path="/dashboard" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/mapping" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <Mapping />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/mapping/:sessionId" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <MappingReview />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/taxonomies" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <Taxonomies />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/taxonomies/new" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <TaxonomyCreate />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/taxonomies/:taxonomyId" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <TaxonomyDetail />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/taxonomies/:taxonomyId/edit" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <TaxonomyEdit />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/settings" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />

        <Route path="/sessions" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <SessionsList />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        <Route path="/usage" element={
          <LazyWrapper>
            <ProtectedRoute>
              <MainLayout>
                <Usage />
              </MainLayout>
            </ProtectedRoute>
          </LazyWrapper>
        } />
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

export default App;